import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    Pagination,
    Skeleton,
} from '@mui/material';
import { FiArrowLeft } from 'react-icons/fi';
import { IoMdAddCircleOutline } from 'react-icons/io';
import { MdViewCarousel, MdViewAgenda, MdTab, MdCategory } from 'react-icons/md';
import { ConfirmDelete, FONT, ProductGrid, StatusPill } from '../../components/MobileContent/contentShared';
import BannerModal from '../../components/MobileContent/BannerModal';
import LayoutCard from '../../components/MobileContent/LayoutCard';
import LayoutForm from '../../components/MobileContent/LayoutForm';
import BannerCard from '../../components/MobileContent/BannerCard';
import TabsCard from '../../components/MobileContent/TabCard';
import TabForm from '../../components/MobileContent/TabForm';
import SubcategoryCard from '../../components/MobileContent/SubcategoryCard';
import SubcategoryForm from '../../components/MobileContent/SubcategoryForm';
import { postData, postFileUpload, putData, getData, deleteData } from '../../Webservice/Webservice';
import { toast } from 'react-toastify';

const contentTabs = [
    { value: 'banner', label: 'Banners', icon: MdViewCarousel, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', hint: 'Promotional images shown at the top of the home screen. Position decides the order.' },
    { value: 'layout', label: 'Layout Sections', icon: MdViewAgenda, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', hint: 'Product rows on the home screen. Open one to see the products inside it.' },
    { value: 'tab', label: 'Tab Section', icon: MdTab, gradient: 'linear-gradient(135deg,#10b981,#059669)', hint: 'Tabs that group products, each shown to a chosen audience.' },
    { value: 'subcategory', label: 'Subcategory Section', icon: MdCategory, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', hint: 'Category shortcuts with an image, linked to a category or subcategory.' },
];

const MobileContent = () => {
    const [activeTab, setActiveTab] = useState('banner');
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [layoutGalleryOpen, setLayoutGalleryOpen] = useState(false);
    const [layoutGalleryData, setLayoutGalleryData] = useState(null);
    const [layoutFormOpen, setLayoutFormOpen] = useState(false);
    const [editingLayout, setEditingLayout] = useState(null);
    const [layouts, setLayouts] = useState([]);
    const [tabsCardDetailsOpen, setTabsCardDetailsOpen] = useState(false);
    const [tabsCardDetailsData, setTabsCardDetailsData] = useState(null);
    const [tabFormOpen, setTabFormOpen] = useState(false);
    const [editingTab, setEditingTab] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [subcategoryFormOpen, setSubcategoryFormOpen] = useState(false);
    const [editingSubcategory, setEditingSubcategory] = useState(null);
    const [subcategories, setSubcategories] = useState([]);
    const [counts, setCounts] = useState({});
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [bannerMeta, setBannerMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
    const [layoutMeta, setLayoutMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
    const [tabMeta, setTabMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
    const [subcategoryMeta, setSubcategoryMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (newValue === 'banner') {
            setBannerMeta(prev => ({ ...prev, page: 1 }));
        } else if (newValue === 'layout') {
            setLayoutMeta(prev => ({ ...prev, page: 1 }));
        } else if (newValue === 'tab') {
            setTabMeta(prev => ({ ...prev, page: 1 }));
        } else if (newValue === 'subcategory') {
            setSubcategoryMeta(prev => ({ ...prev, page: 1 }));
        }
    };

    // Fetch banners with pagination
    const fetchBanners = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/banner/list', { page, limit }, token, false);
            
            if (response.banner && Array.isArray(response.banner)) {
                setBanners(response.banner);
                setBannerMeta(response.meta || { page: 1, totalPages: 1, total: 0, limit });
            } else if (response.banners && Array.isArray(response.banners)) {
                setBanners(response.banners);
                setBannerMeta(response.meta || { page: 1, totalPages: 1, total: 0, limit });
            } else {
                setBanners([]);
                setBannerMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
            setBanners([]);
            setBannerMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch layouts with pagination
    const fetchLayouts = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/layout/list', { page, limit, productLimit: 100 }, token);
            
            if (response && Array.isArray(response.layout)) {
                const transformedLayouts = response.layout.map(layout => ({
                    id: layout._id || layout.id,
                    _id: layout._id,
                    title: layout.title,
                    type: layout.title,
                    layout: layout.layoutId?.name || 'Unknown Layout',
                    layoutId: layout.layoutId?._id || '',
                    position: layout.position,
                    status: (layout.status || '').toLowerCase(),
                    products: Array.isArray(layout.product_id) ? layout.product_id.map(product => ({
                        _id: product._id,
                        name: product.title,
                        title: product.title,
                        image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                    })) : [],
                }));
                setLayouts(transformedLayouts);
                setLayoutMeta(response.meta || layoutMeta);
            } else {
                setLayouts([]);
                setLayoutMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching layouts:', error);
            setLayouts([]);
            setLayoutMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch tabs with pagination
    const fetchTabs = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/tabs/list', { page, limit, productLimit: 100 }, token);
            
            if (response && Array.isArray(response.tabs)) {
                const transformedTabs = response.tabs.map(tab => ({
                    id: tab._id,
                    _id: tab._id,
                    name: tab.name,
                    status: tab.status,
                    position: tab.position,
                    showTo: tab.showTo || 'all',
                    product_ids: Array.isArray(tab.product_id) ? tab.product_id.map(p => p._id || p.id || p) : [],
                    products: Array.isArray(tab.product_id) ? tab.product_id.map(product => ({
                        _id: product._id || product.id || product,
                        id: product._id || product.id || product,
                        name: product.title || product.name,
                        title: product.title || product.name,
                        image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                    })) : [],
                }));
                setTabs(transformedTabs);
                setTabMeta(response.meta || tabMeta);
            } else {
                setTabs([]);
                setTabMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching tabs:', error);
            setTabs([]);
            setTabMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch subcategories with pagination
    const fetchSubcategories = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/homecategory/list', { page, limit }, token);
            
            if (response && Array.isArray(response.homecategory)) {
                setSubcategories(response.homecategory);
                setSubcategoryMeta(response.meta || { page: 1, totalPages: 1, total: 0, limit });
            } else {
                console.error('Incomplete homecategory response:', response);
                setSubcategories([]);
                setSubcategoryMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSubcategories([]);
            setSubcategoryMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch data based on active tab
    useEffect(() => {
        if (activeTab === 'banner') {
            fetchBanners(bannerMeta.page, bannerMeta.limit);
        } else if (activeTab === 'layout') {
            fetchLayouts(layoutMeta.page, layoutMeta.limit);
        } else if (activeTab === 'tab') {
            fetchTabs(tabMeta.page, tabMeta.limit);
        } else if (activeTab === 'subcategory') {
            fetchSubcategories(subcategoryMeta.page, subcategoryMeta.limit);
        }
    }, [activeTab, bannerMeta.page, layoutMeta.page, tabMeta.page, subcategoryMeta.page]);

    useEffect(() => {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const endpoints = { banner: 'homepage/banner/list', layout: 'homepage/layout/list', tab: 'homepage/tabs/list', subcategory: 'homepage/homecategory/list' };
        Object.entries(endpoints).forEach(([key, route]) => {
            getData(route, { page: 1, limit: 1 }, token, false)
                .then((res) => setCounts((prev) => (prev[key] === undefined ? { ...prev, [key]: res?.meta?.total ?? 0 } : prev)))
                .catch(() => {});
        });
    }, []);
    useEffect(() => { setCounts((p) => ({ ...p, banner: bannerMeta.total })); }, [bannerMeta.total]);
    useEffect(() => { setCounts((p) => ({ ...p, layout: layoutMeta.total })); }, [layoutMeta.total]);
    useEffect(() => { setCounts((p) => ({ ...p, tab: tabMeta.total })); }, [tabMeta.total]);
    useEffect(() => { setCounts((p) => ({ ...p, subcategory: subcategoryMeta.total })); }, [subcategoryMeta.total]);

    // Handle page change for banners
    const handleBannerPageChange = (event, newPage) => {
        setBannerMeta(prev => ({ ...prev, page: newPage }));
    };

    // Handle page change for layouts
    const handleLayoutPageChange = (event, newPage) => {
        setLayoutMeta(prev => ({ ...prev, page: newPage }));
    };

    // Handle page change for tabs
    const handleTabPageChange = (event, newPage) => {
        setTabMeta(prev => ({ ...prev, page: newPage }));
    };

    // Handle page change for subcategories
    const handleSubcategoryPageChange = (event, newPage) => {
        setSubcategoryMeta(prev => ({ ...prev, page: newPage }));
    };

    // Banner handlers
    const handleBannerOpen = (banner = null) => {
        setEditingBanner(banner);
        setBannerModalOpen(true);
    };

    const handleBannerClose = () => {
        setBannerModalOpen(false);
        setEditingBanner(null);
    };

    const handleBannerDelete = async (banner) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/banner/delete/${banner._id || banner.id}`, token);
            
            if (response) {
                fetchBanners(bannerMeta.page, bannerMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting banner:', error);
        }
    };

    const handleBannerSubmit = async (bannerData) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            const formData = new FormData();
            if (bannerData.image) {
                if (bannerData.image instanceof File) {
                    formData.append('image', bannerData.image);
                } else {
                    // It's already a URL, send it as a string for persistence
                    formData.append('image', bannerData.image);
                }
            }
            formData.append('type', bannerData.type);
            if (bannerData.type === 'external') formData.append('externalUrl', bannerData.externalUrl || '');
            else formData.append('link', bannerData.link || '');
            formData.append('showTo', bannerData.showTo || 'all');
            formData.append('status', bannerData.status);
            formData.append('position', bannerData.position);

            let response;
            if (editingBanner) {
                formData.append('id', editingBanner._id);
                response = await putData(`homepage/banner/update/${editingBanner._id}`, formData, token);
            } else {
                response = await postFileUpload('homepage/banner/add', formData, token);
            }
            
            if (response) {
                fetchBanners(bannerMeta.page, bannerMeta.limit);
            }
            handleBannerClose();
        } catch (error) {
            console.error('Error handling banner:', error);
        }
    };

    // Layout handlers
    const handleLayoutEdit = (layout) => {
        setEditingLayout(layout);
        setLayoutFormOpen(true);
    };

    const handleLayoutDelete = async (layout) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/layout/delete/${layout._id || layout.id}`, token);
            
            if (response) {
                fetchLayouts(layoutMeta.page, layoutMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting layout:', error);
        }
    };

    const handleLayoutOpenGallery = async (layout) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`homepage/layout/${layout._id || layout.id}`, {}, token, false);
            
            if (response) {
                const rawData = response.layout || response;
                const transformedLayout = {
                    _id: rawData._id,
                    type: rawData.title,
                    status: rawData.status,
                    position: rawData.position,
                    layout: rawData.layoutId?.name || 'Unknown Layout',
                    layoutId: rawData.layoutId?._id || '',
                    products: Array.isArray(rawData.product_id) ? rawData.product_id.map(product => ({
                        _id: product._id,
                        name: product.title,
                        image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                        price: product.price
                    })) : [],
                };
                setLayoutGalleryData(transformedLayout);
            } else {
                 setLayoutGalleryData(layout);
            }
            setLayoutGalleryOpen(true);
        } catch (error) {
            console.error('Error fetching layout details:', error);
            setLayoutGalleryData(layout);
            setLayoutGalleryOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleLayoutCloseGallery = () => {
        setLayoutGalleryOpen(false);
        setLayoutGalleryData(null);
    };

    const handleLayoutOpenForm = () => {
        setEditingLayout(null);
        setLayoutFormOpen(true);
    };

    const handleLayoutCloseForm = () => {
        setLayoutFormOpen(false);
        setEditingLayout(null);
    };

    const handleLayoutSubmit = async (formValues) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            if (!formValues.title || !formValues.layoutId) {
                toast.error("Title and Layout ID are required");
                return;
            }

            const layoutData = {
                title: formValues.title,
                layoutId: formValues.layoutId,
                position: formValues.position,
                product_id: formValues.product_ids,
                status: (formValues.status || 'active').toLowerCase(),
            };

            let response;
            if (editingLayout) {
                response = await putData(`homepage/layout/update/${editingLayout._id || editingLayout.id}`, layoutData, token);
            } else {
                response = await postData('homepage/layout/add', layoutData, token);
            }
            
            if (response) {
                toast.success(editingLayout ? "Layout updated successfully" : "Layout added successfully");
                fetchLayouts(layoutMeta.page, layoutMeta.limit);
                handleLayoutCloseForm();
            }
        } catch (error) {
            console.error('Error handling layout:', error);
        }
    };

    // Tab handlers
    const handleTabOpen = (tab = null) => {
        setEditingTab(tab);
        setTabFormOpen(true);
    };

    const handleTabClose = () => {
        setTabFormOpen(false);
        setEditingTab(null);
    };

    const handleTabSubmit = async (tabData) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            if (!tabData.name) {
                toast.error("Tab name is required");
                return;
            }

            const tabPayload = {
                name: tabData.name,
                status: (tabData.status || 'active').toLowerCase(),
                position: tabData.position,
                showTo: (tabData.showTo || 'all').toLowerCase(),
                product_id: tabData.product_id,
            };

            let response;
            if (editingTab) {
                response = await putData(`homepage/tabs/update/${editingTab._id || editingTab.id}`, tabPayload, token);
            } else {
                response = await postData('homepage/tabs/add', tabPayload, token);
            }
            
            if (response) {
                toast.success(editingTab ? "Tab updated successfully" : "Tab added successfully");
                fetchTabs(tabMeta.page, tabMeta.limit);
                handleTabClose();
            }
        } catch (error) {
            console.error('Error handling tab:', error);
        }
    };

    // Product handlers for Tabs
    const handleProductEdit = (product) => {
        setEditingTab(product);
        setTabFormOpen(true);
    };

    const handleProductDelete = async (product) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/tabs/delete/${product._id || product.id}`, token);
            
            if (response) {
                fetchTabs(tabMeta.page, tabMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting tab:', error);
        }
    };

    const handleTabsCardOpenDetails = async (product) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`homepage/tabs/${product._id || product.id}`, {}, token, false);
            
            if (response) {
                setTabsCardDetailsData(response.tab || response);
            } else {
                 setTabsCardDetailsData(product);
            }
             setTabsCardDetailsOpen(true);
        } catch (error) {
            console.error('Error fetching tab details:', error);
            setTabsCardDetailsData(product);
            setTabsCardDetailsOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleTabsCardCloseDetails = () => {
        setTabsCardDetailsOpen(false);
        setTabsCardDetailsData(null);
    };

    // Subcategory handlers
    const handleSubcategoryOpen = (subcategory = null) => {
        setEditingSubcategory(subcategory);
        setSubcategoryFormOpen(true);
    };

    const handleSubcategoryClose = () => {
        setSubcategoryFormOpen(false);
        setEditingSubcategory(null);
    };

    const handleSubcategorySubmit = async (subcategoryData) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            if (!subcategoryData.linkId) {
                toast.error("Please select a category/subcategory");
                return;
            }

            if (!editingSubcategory && !subcategoryData.image) {
                toast.error("Image is required for new items");
                return;
            }

            const formData = new FormData();
            if (subcategoryData.image) {
                if (subcategoryData.image instanceof File) {
                    formData.append('image', subcategoryData.image);
                } else {
                    // It's a URL string, send it back for persistence
                    formData.append('image', subcategoryData.image);
                }
            }
            formData.append('type', subcategoryData.type);
            formData.append('linkId', subcategoryData.linkId);
            formData.append('status', (subcategoryData.status || 'active').toLowerCase());
            formData.append('showTo', (subcategoryData.showTo || 'all').toLowerCase());
            formData.append('position', subcategoryData.position);

            let response;
            if (editingSubcategory) {
                formData.append('id', editingSubcategory._id);
                response = await putData(`homepage/homecategory/update/${editingSubcategory._id}`, formData, token);
            } else {
                response = await postFileUpload('homepage/homecategory/add', formData, token);
            }
            
            if (response) {
                toast.success(editingSubcategory ? "Home Category updated successfully" : "Home Category added successfully");
                fetchSubcategories(subcategoryMeta.page, subcategoryMeta.limit);
                handleSubcategoryClose();
            }
        } catch (error) {
            console.error('Error handling subcategory:', error);
        }
    };

    const handleSubcategoryDelete = async (subcategory) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/homecategory/delete/${subcategory._id || subcategory.id}`, token);
            
            if (response) {
                fetchSubcategories(subcategoryMeta.page, subcategoryMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting subcategory:', error);
        }
    };

    // Get active datafhandleLayoutOpenGallery
    const getActiveData = () => {
        switch (activeTab) {
            case 'banner':
                return banners;
            case 'layout':
                return layouts;
            case 'tab':
                return tabs;
            case 'subcategory':
                return subcategories;
            default:
                return banners;
        }
    };

    // Get add button text
    const getAddButtonText = () => {
        switch (activeTab) {
            case 'banner':
                return 'Add Banner';
            case 'layout':
                return 'Add Layout';
            case 'tab':
                return 'Add Tabs';
            case 'subcategory':
                return 'Add Home Category';
            default:
                return 'Add Content';
        }
    };

    const activeData = getActiveData();

    const activeMeta = { banner: bannerMeta, layout: layoutMeta, tab: tabMeta, subcategory: subcategoryMeta }[activeTab];
    const activePageChange = { banner: handleBannerPageChange, layout: handleLayoutPageChange, tab: handleTabPageChange, subcategory: handleSubcategoryPageChange }[activeTab];
    const activeDef = contentTabs.find((t) => t.value === activeTab);
    const gridSizes = {
        banner: { xs: 12, sm: 6, xl: 4 },
        layout: { xs: 12, lg: 6 },
        tab: { xs: 12, sm: 6, xl: 4 },
        subcategory: { xs: 12, sm: 6, md: 4, xl: 3 },
    }[activeTab];

    // Delete goes through a confirmation dialog for every tab.
    const requestDelete = (kind, item, label) => setDeleteTarget({ kind, item, label });
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const { kind, item } = deleteTarget;
            if (kind === 'banner') await handleBannerDelete(item);
            else if (kind === 'layout') await handleLayoutDelete(item);
            else if (kind === 'tab') await handleProductDelete(item);
            else await handleSubcategoryDelete(item);
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };
    const deleteNoun = { banner: 'banner', layout: 'layout section', tab: 'tab', subcategory: 'home category' }[deleteTarget?.kind] || 'item';

    const renderTabCard = (item) => {
        switch (activeTab) {
            case 'banner':
                return <BannerCard banner={item} onEdit={handleBannerOpen} onDelete={(b) => requestDelete('banner', b, `${b.type || 'Banner'} banner`)} />;
            case 'layout':
                return <LayoutCard layout={item} onEdit={handleLayoutEdit} onDelete={(l) => requestDelete('layout', l, l.title || l.type)} onOpenGallery={handleLayoutOpenGallery} />;
            case 'tab':
                return <TabsCard product={item} onEdit={handleProductEdit} onDelete={(t) => requestDelete('tab', t, t.name)} onOpenDetails={handleTabsCardOpenDetails} />;
            default:
                return <SubcategoryCard subcategory={item} onEdit={handleSubcategoryOpen} onDelete={(s) => requestDelete('subcategory', s, s.linkId?.title)} />;
        }
    };

    const DetailShell = ({ backLabel, onBack, title, subtitle, status, children }) => (
        <Box sx={{ p: { xs: 1.5, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <Button startIcon={<FiArrowLeft />} onClick={onBack} sx={{ mb: 2, textTransform: 'none', fontWeight: 700, color: '#475569' }}>
                {backLabel}
            </Button>
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '16px', color: '#fff', background: 'linear-gradient(135deg,#0f172a,#334155)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5 }}>{title}</Typography>
                    {subtitle && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.85 }}>{subtitle}</Typography>}
                </Box>
                <StatusPill status={status} />
            </Paper>
            {children}
        </Box>
    );

    // Layout gallery view
    if (layoutGalleryOpen && layoutGalleryData) {
        const galleryItems = Array.isArray(layoutGalleryData?.products)
            ? layoutGalleryData.products.map((product, index) => ({ key: index, image: product.image, name: product.name }))
            : [];
        return (
            <DetailShell backLabel="Back to layouts" onBack={handleLayoutCloseGallery} title={layoutGalleryData?.type} subtitle={`${layoutGalleryData?.layout} · ${galleryItems.length} products`} status={layoutGalleryData?.status}>
                <ProductGrid items={galleryItems} emptyText="No products available for this layout." />
            </DetailShell>
        );
    }

    // Tab products view
    if (tabsCardDetailsOpen && tabsCardDetailsData) {
        const productItems = Array.isArray(tabsCardDetailsData?.product_id)
            ? tabsCardDetailsData.product_id.map((product, index) => ({
                key: index,
                image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                name: product.title,
            }))
            : [];
        return (
            <DetailShell backLabel="Back to tabs" onBack={handleTabsCardCloseDetails} title={tabsCardDetailsData?.name} subtitle={`${productItems.length} products`} status={tabsCardDetailsData?.status}>
                <ProductGrid items={productItems} emptyText="No products available for this tab." />
            </DetailShell>
        );
    }

    const formOpen = (activeTab === 'layout' && layoutFormOpen) || (activeTab === 'tab' && tabFormOpen);

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>Mobile Content</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Control what customers see on the app's home screen.</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<IoMdAddCircleOutline />}
                    onClick={() => {
                        if (activeTab === 'banner') handleBannerOpen();
                        else if (activeTab === 'layout') handleLayoutOpenForm();
                        else if (activeTab === 'tab') handleTabOpen();
                        else handleSubcategoryOpen();
                    }}
                    sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}
                >
                    {getAddButtonText()}
                </Button>
            </Box>

            {/* Section switcher: each tab is a coloured card with its item count */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
                {contentTabs.map((tab) => (
                    <Paper
                        key={tab.value}
                        elevation={0}
                        onClick={() => handleTabChange(null, tab.value)}
                        sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: tab.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: activeTab === tab.value ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
                    >
                        <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><tab.icon size={26} /></Box>
                        <Box>
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{tab.label}</Typography>
                            <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{counts[tab.value] ?? '–'}</Typography>
                        </Box>
                    </Paper>
                ))}
            </Box>

            {!formOpen && (
                <>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mb: 2 }}>{activeDef?.hint}</Typography>
                    {loading ? (
                        <Grid container spacing={2.5}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Grid item key={i} {...gridSizes}><Skeleton variant="rounded" height={activeTab === 'layout' ? 110 : 230} sx={{ borderRadius: '16px' }} /></Grid>
                            ))}
                        </Grid>
                    ) : activeData.length > 0 ? (
                        <Grid container spacing={2.5}>
                            {activeData.map((item) => (
                                <Grid item key={item._id || item.id} {...gridSizes}>{renderTabCard(item)}</Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Paper elevation={0} sx={{ py: 8, textAlign: 'center', borderRadius: '14px', border: '1px dashed #cbd5e1' }}>
                            <Typography sx={{ fontFamily: FONT, fontWeight: 800, color: '#334155', fontSize: 15.5 }}>No {activeDef?.label.toLowerCase()} yet</Typography>
                            <Typography sx={{ fontFamily: FONT, color: '#64748b', mt: 0.5 }}>Use “{getAddButtonText()}” to create the first one.</Typography>
                        </Paper>
                    )}

                    {activeMeta.total > 0 && (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Pagination
                                    count={activeMeta.totalPages}
                                    page={activeMeta.page}
                                    onChange={activePageChange}
                                    shape="rounded"
                                    disabled={loading}
                                    showFirstButton
                                    showLastButton
                                    sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' }, '& .MuiPaginationItem-root': { fontFamily: FONT, fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }}
                                />
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center', fontFamily: FONT }}>
                                Showing {(activeMeta.page - 1) * activeMeta.limit + 1} to {Math.min(activeMeta.page * activeMeta.limit, activeMeta.total)} of {activeMeta.total} {activeDef?.label.toLowerCase()}
                            </Typography>
                        </>
                    )}
                </>
            )}

            <BannerModal
                open={bannerModalOpen}
                handleClose={handleBannerClose}
                handleSubmit={handleBannerSubmit}
                isEdit={!!editingBanner}
                editingBanner={editingBanner}
            />

            {activeTab === 'layout' && layoutFormOpen && (
                <Box sx={{ mb: 2 }}>
                    <LayoutForm
                        open={true}
                        inline={true}
                        handleClose={handleLayoutCloseForm}
                        handleSubmit={handleLayoutSubmit}
                        isEdit={!!editingLayout}
                        editingLayout={editingLayout}
                    />
                </Box>
            )}

            {activeTab === 'tab' && tabFormOpen && (
                <Box sx={{ mb: 2 }}>
                    <TabForm
                        open={true}
                        inline={true}
                        handleClose={handleTabClose}
                        handleSubmit={handleTabSubmit}
                        isEdit={!!editingTab}
                        editingTab={editingTab}
                    />
                </Box>
            )}

            <SubcategoryForm
                open={subcategoryFormOpen}
                handleClose={handleSubcategoryClose}
                handleSubmit={handleSubcategorySubmit}
                isEdit={!!editingSubcategory}
                editingSubcategory={editingSubcategory}
            />

            <ConfirmDelete target={deleteTarget} noun={deleteNoun} label={deleteTarget?.label} busy={deleting} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
        </Box>
    );
};

export default MobileContent;
